import { useState, useEffect, useCallback } from "react";
import API, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Loader2, KeyRound, Trash2, Power, PowerOff, ShieldAlert, Mail, Phone, User } from "lucide-react";
import { toast } from "sonner";

export default function AdminAdminUsers() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", mobile: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [error, setError] = useState("");

  const isSuper = !!currentUser?.is_super_admin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/admin-users");
      setAdmins(data.admins || []);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || "Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuper) load(); else setLoading(false); }, [isSuper, load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await API.post("/admin/admin-users", form);
      toast.success("Admin user created");
      setShowCreate(false);
      setForm({ full_name: "", email: "", password: "", mobile: "" });
      load();
    } catch (e2) {
      toast.error(formatError(e2.response?.data?.detail) || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await API.put(`/admin/admin-users/${a.id}`, { is_active: !(a.is_active !== false) });
      toast.success(a.is_active !== false ? "Admin deactivated" : "Admin activated");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete admin "${a.full_name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/admin/admin-users/${a.id}`);
      toast.success("Admin deleted");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const resetPassword = async () => {
    if (!pwValue || pwValue.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwSaving(true);
    try {
      await API.put(`/admin/admin-users/${pwTarget.id}`, { password: pwValue });
      toast.success("Password updated");
      setPwTarget(null);
      setPwValue("");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to update password");
    } finally {
      setPwSaving(false);
    }
  };

  if (!isSuper) {
    return (
      <div className="max-w-2xl" data-testid="admin-users-no-access">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
          <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-amber-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Super Admin access required</h2>
            <p className="text-sm text-amber-800 mt-1">Only the primary super admin can manage admin users. Contact your super admin if you need to add or modify accounts.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-admin-users-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
            <ShieldCheck className="w-6 h-6 text-[#008A99]" /> Admin Users
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage admin portal accounts. All admins have full rights to the admin panel.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#008A99] hover:bg-[#006670] rounded-xl h-11 px-5" data-testid="create-admin-button">
          <Plus className="w-4 h-4 mr-2" /> New Admin
        </Button>
      </div>

      {error && <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : admins.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No admin users yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Mobile</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const active = a.is_active !== false;
                const isSelf = a.id === currentUser?.id;
                return (
                  <tr key={a.id} className="border-t border-gray-100" data-testid={`admin-row-${a.email}`}>
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {a.full_name}
                        {a.is_super_admin && <span className="text-[9px] bg-[#008A99] text-white px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Super</span>}
                        {isSelf && <span className="text-[9px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">You</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{a.email}</td>
                    <td className="px-5 py-3.5 text-gray-600">{a.mobile || "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-gray-400"}`} />
                        {active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setPwTarget(a)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#008A99] transition" title="Reset password" data-testid={`reset-pw-${a.email}`}>
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {!a.is_super_admin && !isSelf && (
                          <button onClick={() => toggleActive(a)} className={`p-2 rounded-lg hover:bg-gray-100 transition ${active ? "text-amber-600" : "text-emerald-600"}`} title={active ? "Deactivate" : "Activate"} data-testid={`toggle-active-${a.email}`}>
                            {active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </button>
                        )}
                        {!a.is_super_admin && !isSelf && (
                          <button onClick={() => handleDelete(a)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition" title="Delete" data-testid={`delete-admin-${a.email}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Create new admin user</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2" data-testid="create-admin-form">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Full name</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Doe" className="pl-10 h-11 rounded-xl" data-testid="new-admin-name" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Email (used as username)</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@transasia.com" className="pl-10 h-11 rounded-xl" data-testid="new-admin-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Password</label>
              <Input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="h-11 rounded-xl" data-testid="new-admin-password" />
              <p className="text-[11px] text-gray-400 mt-1.5">Share this password securely with the new admin. They can change it after their first login.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Mobile (optional)</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876543210" className="pl-10 h-11 rounded-xl" data-testid="new-admin-mobile" />
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-[#008A99] hover:bg-[#006670] rounded-xl" data-testid="submit-create-admin">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create admin"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setPwValue(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Reset password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-gray-600">Setting a new password for <span className="font-semibold text-gray-900">{pwTarget?.email}</span></div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">New password</label>
              <Input type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="At least 6 characters" className="h-11 rounded-xl" data-testid="reset-pw-input" autoFocus />
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => { setPwTarget(null); setPwValue(""); }} className="rounded-xl">Cancel</Button>
              <Button type="button" onClick={resetPassword} disabled={pwSaving} className="bg-[#008A99] hover:bg-[#006670] rounded-xl" data-testid="submit-reset-pw">
                {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
